// "Why this track?" — a small disclosure that explains why a track was queued.
//
// Reasons are derived client-side from track tags (station, mood, energy,
// genre, recency, like count) and the user's local reactions. We deliberately
// keep this lightweight (no extra API call) so it never blocks playback.
//
// Renders nothing when there are no meaningful reasons or no current track.

import React, { useState, useRef, useEffect } from "react";
import { Info, X } from "lucide-react";

function buildReasons({ track, stationName, reactions }) {
  if (!track) return [];
  const reasons = [];
  if (stationName) reasons.push(`Tuned to ${stationName}`);
  if (track.mood) reasons.push(track.mood.charAt(0).toUpperCase() + track.mood.slice(1));
  if (track.energy) reasons.push(`Energy ${track.energy}/5`);
  if (track.genre) reasons.push(track.genre);

  // Recently approved → within the last 14 days.
  if (track.approved_at) {
    const d = new Date(track.approved_at);
    const ageDays = (Date.now() - d.getTime()) / (1000 * 60 * 60 * 24);
    if (ageDays <= 14) reasons.push("Recently approved");
  }
  if ((track.like_count || 0) >= 3) reasons.push("Trending — listeners are loving it");
  if (track.featured) reasons.push("Editor's pick");

  // Personalization signal: have they thumbed-up this track or others?
  const myReaction = reactions ? reactions[track.id] : null;
  if (myReaction === "like") reasons.push("You thumbed this up");
  return reasons;
}

export default function WhyThisTrack({ track, stationName, reactions }) {
  const [open, setOpen] = useState(false);
  const popRef = useRef(null);

  // Close on outside click / Esc.
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const reasons = buildReasons({ track, stationName, reactions });
  if (!track || reasons.length === 0) return null;

  return (
    <div className="relative inline-block" ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="why-this-track-toggle"
        title="Why this track?"
        aria-expanded={open}
        aria-label="Why this track?"
        className="overline text-[10px] px-2 py-0.5 rounded-full border border-[#22262E] text-[#A1A4AB] hover:text-[#FDFBF7] hover:border-[#C5A059]/40 inline-flex items-center gap-1 transition-colors"
      >
        <Info className="w-3 h-3" strokeWidth={1.5} /> Why this track?
      </button>
      {open && (
        <div
          data-testid="why-this-track-panel"
          role="dialog"
          aria-label="Reasons this track was queued"
          className="absolute z-30 mt-2 left-0 md:left-auto md:right-0 w-[280px] rounded-xl border border-[#C5A059]/30 bg-[#0E1014]/95 backdrop-blur-md shadow-[0_0_40px_rgba(0,0,0,0.6)] p-4"
        >
          <div className="flex items-start justify-between gap-2 mb-2">
            <div className="overline text-[#C5A059] text-[10px]">Why this track?</div>
            <button
              type="button"
              onClick={() => setOpen(false)}
              data-testid="why-this-track-close"
              aria-label="Close"
              className="text-[#A1A4AB] hover:text-[#FDFBF7]"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.5} />
            </button>
          </div>
          <ul className="space-y-1.5 text-sm text-[#FDFBF7]">
            {reasons.map((r) => (
              <li key={r} className="flex items-start gap-2">
                <span className="mt-1 w-1 h-1 rounded-full bg-[#C5A059] flex-shrink-0" />
                <span className="leading-snug">{r}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
